import { defineStore } from 'pinia'
import { ref } from 'vue'

import { mailApi } from './api'

/**
 * The unread count beside «Correo» in the menu. A plain ref and not store state
 * because the menu entry is built when the app loads, before any store exists:
 * the entry carries this ref and the menu reads it (AppLayout `badgeOf`).
 */
export const unreadMail = ref(0)

let seq = 0

/**
 * What the whole app shares about mail: the unread count, and the composer —
 * which any screen can open («Enviar por email» on an invoice) without leaving
 * where it is. The composer itself is drawn once, by MailComposeHost, in the
 * app's layout.
 */
export const useMailStore = defineStore('mail-client', () => {
  /** The composer that is open, or null. Its options say what it starts from. */
  const composer = ref(null)
  /** Bumped when something was sent or saved, so an open list reloads. */
  const changed = ref(0)
  /** Asked while another is open: the host saves that one first (see MailComposeHost). */
  const pending = ref(null)

  async function refreshUnread() {
    try {
      unreadMail.value = (await mailApi.unread()).unread ?? 0
    } catch {
      // The count is a courtesy: a failure must not break the page.
    }
  }

  /**
   * Open the composer.
   *
   * @param {object} options
   * @param {'new'|'reply'|'replyAll'|'forward'|'draft'} [options.mode]
   * @param {object} [options.message]   the message answered or forwarded (its detail)
   * @param {string} [options.draftId]   a draft to reopen
   * @param {{type: string, referenceId: string}} [options.context]  the record it is sent from
   * @param {Array} [options.to]         [{ name, address }] or strings
   * @param {string} [options.subject]
   * @param {string} [options.html]
   * @param {string} [options.accountId]
   * @param {Function} [options.onSent]  called with the send result
   */
  function compose(options = {}) {
    const next = { key: ++seq, minimized: false, ...options }
    if (composer.value) pending.value = next
    else composer.value = next
  }

  /** The host has put the open one away: show the one that was waiting. */
  function takePending() {
    composer.value = pending.value
    pending.value = null
  }

  function close() {
    composer.value = null
  }

  function touched() {
    changed.value += 1
    refreshUnread()
  }

  return { composer, pending, changed, compose, takePending, close, touched, refreshUnread }
})

/**
 * For a module's screen: open the composer on one of its records.
 *
 *   const { sendByEmail } = useMailCompose()
 *   sendByEmail('invoice', invoice.id, { onSent: reload })
 *   sendByEmail('quote', quote.id, { options: { labels, locale }, subject, html })
 *
 * `options` goes back, untouched, to the module that registered the record's
 * context — the only one that knows what it means.
 */
export function useMailCompose() {
  const store = useMailStore()
  return {
    compose: store.compose,
    sendByEmail: (type, referenceId, { options = null, ...rest } = {}) =>
      store.compose({ mode: 'new', context: { type, referenceId: String(referenceId), options }, ...rest }),
  }
}
